/**
 * Tab Manager Module
 * Handles layer tabs in the attribute table
 */

import { displayAttributeTable, hideTable } from './tableManager.js';

// DOM References
const tableTabs = document.getElementById('table-tabs');
const tableTabsLeft = tableTabs.querySelector('.table-tabs-left');

// State
let activeLayerId = null;
let onTabSwitch = null; // Callback when tab is switched
let onTabRemove = null; // Callback when tab is removed

/**
 * Initialize tab manager with callbacks
 * @param {Function} switchCallback - Called when switching tabs
 * @param {Function} removeCallback - Called when removing a tab
 */
export function initializeTabManager(switchCallback, removeCallback) {
    onTabSwitch = switchCallback;
    onTabRemove = removeCallback;
}

/**
 * Create a tab for a layer
 * @param {Object} layer - Layer object with id, name, color
 */
export function createLayerTab(layer) {
    const tab = document.createElement('button');
    tab.className = 'table-tab';
    tab.dataset.layerId = layer.id;

    // Color indicator
    const colorDiv = document.createElement('div');
    colorDiv.className = 'table-tab-color';
    colorDiv.style.backgroundColor = layer.color;

    // Layer name
    const nameSpan = document.createElement('span');
    nameSpan.textContent = layer.name;

    // Close button
    const closeSpan = document.createElement('span');
    closeSpan.className = 'table-tab-close';
    closeSpan.textContent = '×';
    closeSpan.onclick = (e) => {
        e.stopPropagation();
        if (onTabRemove) {
            onTabRemove(layer.id);
        }
    };

    tab.appendChild(colorDiv);
    tab.appendChild(nameSpan);
    tab.appendChild(closeSpan);

    // Tab click handler
    tab.onclick = (e) => {
        if (e.target === closeSpan) return;
        switchToLayerTab(layer.id);
    };

    tableTabsLeft.appendChild(tab);
}

/**
 * Switch to a layer's tab
 * @param {string} layerId - Layer ID to switch to
 */
export function switchToLayerTab(layerId) {
    // Update active tab styling
    tableTabsLeft.querySelectorAll('.table-tab').forEach(tab => {
        if (tab.dataset.layerId === layerId) {
            tab.classList.add('active');
        } else {
            tab.classList.remove('active');
        }
    });

    // Update active layer ID
    activeLayerId = layerId;

    // Call switch callback
    if (onTabSwitch) {
        onTabSwitch(layerId);
    }
}

/**
 * Remove a layer's tab
 * @param {string} layerId - Layer ID
 * @param {Array} remainingLayers - Array of remaining layers
 */
export function removeLayerTab(layerId, remainingLayers) {
    const tab = tableTabsLeft.querySelector(`[data-layer-id="${layerId}"]`);
    if (tab) {
        tab.remove();
    }

    // If this was the active tab, switch to another tab
    if (activeLayerId === layerId) {
        if (remainingLayers && remainingLayers.length > 0) {
            // Find another layer to switch to (excluding the one being removed)
            const otherLayer = remainingLayers.find(l => l.id !== layerId);
            if (otherLayer) {
                switchToLayerTab(otherLayer.id);
            } else {
                activeLayerId = null;
            }
        } else {
            activeLayerId = null;
            // Hide table when no layers remain
            hideTable();
        }
    }
}

/**
 * Clear all tabs
 */
export function clearAllTabs() {
    tableTabsLeft.innerHTML = '';
    activeLayerId = null;
    // Hide table when all tabs are cleared
    hideTable();
}

/**
 * Get active layer ID
 */
export function getActiveLayerId() {
    return activeLayerId;
}

/**
 * Check if a layer tab exists
 * @param {string} layerId - Layer ID to check
 */
export function hasTab(layerId) {
    return tableTabsLeft.querySelector(`[data-layer-id="${layerId}"]`) !== null;
}

// Download button functionality moved to legend
